import { useEffect, useState } from "react";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { gql } from "../../../__generated__";
import clsx from "clsx";
import { notify, notifyError } from "../../../utils/toast";
import { GET_STORE_LIST } from "../store-signup-request/page";
import { AdminListDetailLayout } from "../components/admin-list-detail-layout";
import LoadingSpinnerInline from "../../../components/common/loading-spinner/loading-spinner-inline";
import { AdminStoreTableWithMobileCards } from "../components/admin-store-table-with-mobile-cards";
import { useScrollToDetailPanelOnSelect } from "../hooks/use-scroll-to-detail-panel";
import type {
  GetStoreListQuery,
  LiveOrdersByStoreIdQuery,
  CancelOrderByAdminMutation,
  CancelOrderByAdminMutationVariables,
  CancelLiveOrderItemsByAdminMutation,
  CancelLiveOrderItemsByAdminMutationVariables,
} from "../../../__generated__/graphql";

const LIVE_ORDERS_BY_STORE_ID = gql(`
  query liveOrdersByStoreId($storeId: Int!) {
    liveOrdersByStoreId(storeId: $storeId) {
      id
      orderId
      identificationCode
      user {
        id
        name
        phone
      }
      totalQuantity
      amount
      createdAt
      settleId
      settle {
        state
      }
      payoutId
      payout {
        tossPayoutStatus
      }
      orderItems {
        id
        quantity
        product {
          id
          name
          store {
            title
          }
        }
      }
    }
  }
`);

const CANCEL_ORDER_BY_ADMIN = gql(`
  mutation CancelOrderByAdmin($orderId: Int!, $cancelReason: String!) {
    cancelOrderByAdmin(orderId: $orderId, cancelReason: $cancelReason) {
      ok
      error
    }
  }
`);

const CANCEL_LIVE_ORDER_ITEMS_BY_ADMIN = gql(`
  mutation CancelLiveOrderItemsByAdmin(
    $orderId: Int!
    $items: [CancelLiveOrderItemInput!]!
    $cancelReason: String!
  ) {
    cancelLiveOrderItemsByAdmin(
      orderId: $orderId
      items: $items
      cancelReason: $cancelReason
    ) {
      ok
      error
    }
  }
`);

type StoreRow = NonNullable<
  NonNullable<GetStoreListQuery["getStoreList"]>["result"]
>[number];

type LiveOrder = LiveOrdersByStoreIdQuery["liveOrdersByStoreId"][number];

function isSettlementOrPayoutBlockingRefund(order: LiveOrder): boolean {
  if (order.settleId != null) {
    if (order.settle?.state !== "거절") {
      return true;
    }
  }
  if (order.payoutId != null) {
    const st = order.payout?.tossPayoutStatus;
    if (st === "FAILED") return false;
    return true;
  }
  return false;
}

export default function AdminCancelLiveOrder() {
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedStore, setSelectedStore] = useState<StoreRow | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [partialLineQtyStr, setPartialLineQtyStr] = useState<
    Record<number, string>
  >({});

  useScrollToDetailPanelOnSelect(selectedStore);

  const { data: storeData, loading: storeLoading, error: storeError } =
    useQuery<GetStoreListQuery>(GET_STORE_LIST);

  const [
    getLiveOrders,
    {
      data: ordersData,
      loading: ordersLoading,
      refetch: refetchOrders,
    },
  ] = useLazyQuery<LiveOrdersByStoreIdQuery>(LIVE_ORDERS_BY_STORE_ID, {
    variables: { storeId: selectedStore?.id ?? 0 },
  });

  const [cancelOrder] = useMutation<
    CancelOrderByAdminMutation,
    CancelOrderByAdminMutationVariables
  >(CANCEL_ORDER_BY_ADMIN);

  const [cancelLiveOrderItems] = useMutation<
    CancelLiveOrderItemsByAdminMutation,
    CancelLiveOrderItemsByAdminMutationVariables
  >(CANCEL_LIVE_ORDER_ITEMS_BY_ADMIN);

  useEffect(() => {
    setPartialLineQtyStr({});
  }, [selectedOrderId]);

  useEffect(() => {
    if (selectedStore?.id) {
      getLiveOrders({ variables: { storeId: selectedStore.id } });
    }
  }, [selectedStore?.id, getLiveOrders]);

  const liveOrders = ordersData?.liveOrdersByStoreId ?? [];

  const fullList =
    storeData?.getStoreList?.ok ? storeData.getStoreList.result ?? [] : [];
  const keyword = searchKeyword.trim().toLowerCase();
  const storeList = keyword
    ? fullList.filter(
        (store) =>
          store.title.toLowerCase().includes(keyword) ||
          (store.address?.toLowerCase().includes(keyword) ?? false),
      )
    : fullList;

  const handleSelectStore = (store: StoreRow) => {
    setSelectedStore(store);
    setSelectedOrderId(null);
    setCancelReason("");
    setPartialLineQtyStr({});
  };

  const selectedOrder =
    selectedOrderId == null
      ? null
      : (liveOrders.find((o: LiveOrder) => o.id === selectedOrderId) ?? null);

  const settlementBlocked = selectedOrder
    ? isSettlementOrPayoutBlockingRefund(selectedOrder)
    : false;

  const partialItemsBuilt =
    selectedOrder == null
      ? []
      : Object.entries(partialLineQtyStr)
          .map(([idStr, qStr]) => ({
            orderItemId: Number(idStr),
            quantity: parseInt(qStr.trim(), 10),
            line: selectedOrder.orderItems?.find((x) => x.id === Number(idStr)),
          }))
          .filter(
            (r): r is typeof r & { line: NonNullable<typeof r.line> } =>
              r.line != null && !Number.isNaN(r.quantity) && r.quantity > 0,
          );

  const partialInvalidQty =
    selectedOrder == null
      ? false
      : Object.entries(partialLineQtyStr).some(([idStr, qStr]) => {
          const t = qStr.trim();
          if (t === "") return false;
          const qty = parseInt(t, 10);
          const line = selectedOrder.orderItems?.find(
            (x) => x.id === Number(idStr),
          );
          if (!line) return true;
          if (Number.isNaN(qty) || qty < 0) return true;
          return qty > line.quantity;
        });

  const partialHasSelection = partialItemsBuilt.length > 0;

  const partialTotalCancelQty = partialItemsBuilt.reduce((s, r) => s + r.quantity, 0);

  const partialLeavesAtLeastOneQty =
    selectedOrder != null &&
    partialHasSelection &&
    selectedOrder.totalQuantity - partialTotalCancelQty >= 1;

  const partialCanSubmit =
    selectedOrder != null &&
    settlementBlocked === false &&
    cancelReason.trim().length >= 4 &&
    partialHasSelection &&
    partialInvalidQty === false &&
    partialLeavesAtLeastOneQty;

  const handlePartialMenuCancel = async () => {
    if (!selectedOrderId || !partialCanSubmit || !cancelReason.trim()) return;
    if (
      !window.confirm(
        "선택한 메뉴 수량만 부분 환불할까요? 결제사(PG)에서 부분취소가 진행됩니다.",
      )
    ) {
      return;
    }
    await cancelLiveOrderItems({
      variables: {
        orderId: selectedOrderId,
        items: partialItemsBuilt.map(({ orderItemId, quantity }) => ({
          orderItemId,
          quantity,
        })),
        cancelReason: cancelReason.trim(),
      },
      onCompleted: (res) => {
        if (res.cancelLiveOrderItemsByAdmin.ok) {
          notify("선택 메뉴 부분환불이 처리되었습니다.");
          setSelectedOrderId(null);
          setCancelReason("");
          setPartialLineQtyStr({});
          refetchOrders();
        } else {
          notifyError(
            res.cancelLiveOrderItemsByAdmin.error ?? "부분환불에 실패했습니다.",
          );
          refetchOrders();
        }
      },
      onError: () => {
        refetchOrders();
      },
    });
  };

  const handleCancelOrder = async (orderId: number) => {
    const order = liveOrders.find((x: LiveOrder) => x.id === orderId);
    if (order && isSettlementOrPayoutBlockingRefund(order)) {
      notifyError(
        "정산/지급이 진행된 주문은 여기서 전체 취소할 수 없습니다. 운영/정산팀과 조율해 주세요.",
      );
      return;
    }
    if (!cancelReason.trim()) {
      notifyError("취소 사유를 입력해주세요.");
      return;
    }
    if (cancelReason.length < 4) {
      notifyError("취소 사유는 4글자 이상 입력해주세요.");
      return;
    }
    if (
      !window.confirm(
        "해당 실시간 주문을 취소할까요? 결제사(PG) 환불이 진행됩니다.",
      )
    ) {
      return;
    }
    await cancelOrder({
      variables: { orderId, cancelReason },
      onCompleted: (res) => {
        if (res.cancelOrderByAdmin.ok) {
          notify("실시간 주문이 취소되었습니다.");
          setSelectedOrderId(null);
          setCancelReason("");
          refetchOrders();
        } else {
          notifyError(
            res.cancelOrderByAdmin.error ?? "취소에 실패했습니다.",
          );
          refetchOrders();
        }
      },
      onError: () => {
        refetchOrders();
      },
    });
  };

  if (storeLoading) {
    return (
      <div className="w-full p-4 flex justify-center py-8">
        <LoadingSpinnerInline />
      </div>
    );
  }
  if (storeError) {
    return (
      <div className="w-full p-4 text-red-600">
        에러: {storeError.message}
      </div>
    );
  }
  if (!storeData?.getStoreList?.ok) {
    return (
      <div className="w-full p-4 text-red-600">
        {storeData?.getStoreList?.error ?? "매장 목록을 불러올 수 없습니다."}
      </div>
    );
  }

  return (
    <AdminListDetailLayout
      title="실시간 판매 취소"
      description="결제사 환불 · DB 수정 · 쿠폰 취소 (실시간 주문만 조회). 매장 선택 후 주문을 선택하고 취소 사유를 입력하세요."
      leftPanel={{
        title: "매장 목록",
        searchPlaceholder: "매장명·주소로 검색",
        searchValue: searchKeyword,
        onSearchChange: setSearchKeyword,
        children: (
          <AdminStoreTableWithMobileCards
            stores={storeList.map((s) => ({
              id: s.id,
              title: s.title,
              img: s.img,
              address: s.address ?? undefined,
            }))}
            selectionMode="single"
            selectedIds={selectedStore ? [selectedStore.id] : []}
            onSelect={(store) => {
              const full = storeList.find((x) => x.id === store.id);
              if (full) handleSelectStore(full);
            }}
            emptyText={
              keyword
                ? "검색 결과가 없습니다."
                : "등록된 매장이 없습니다."
            }
          />
        ),
      }}
      rightPanel={{
        title: "실시간 주문 취소",
        emptyText: "왼쪽에서 매장을 선택하세요.",
        children: selectedStore ? (
          <div className="space-y-4">
            {ordersLoading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinnerInline />
              </div>
            ) : liveOrders.length === 0 ? (
              <p className="text-gray-500 text-sm">
                취소 가능한 실시간 주문이 없습니다.
              </p>
            ) : (
              <>
                <div className="text-sm text-gray-700">
                  <span className="font-medium">선택 매장:</span>{" "}
                  {selectedStore.title}
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead>
                      <tr>
                        <th className="px-2 py-2 text-left font-medium text-gray-500">
                          주문코드
                        </th>
                        <th className="px-2 py-2 text-left font-medium text-gray-500">
                          주문자
                        </th>
                        <th className="px-2 py-2 text-left font-medium text-gray-500">
                          상품
                        </th>
                        <th className="px-2 py-2 text-left font-medium text-gray-500">
                          주문일시
                        </th>
                        <th className="px-2 py-2 text-left font-medium text-gray-500">
                          금액
                        </th>
                        <th className="px-2 py-2 text-left font-medium text-gray-500">
                          선택
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {liveOrders.map((order: LiveOrder) => (
                        <tr key={order.id}>
                          <td className="px-2 py-2 font-mono text-xs text-blue-600">
                            {order.identificationCode ?? order.orderId}
                          </td>
                          <td className="px-2 py-2">
                            {order.user?.name ?? "—"} ({order.user?.phone ?? "—"}
                            )
                          </td>
                          <td className="px-2 py-2 text-xs">
                            {order.orderItems
                              ?.map(
                                (oi: { product?: { name?: string }; quantity: number }) =>
                                  `${oi.product?.name ?? ""} x${oi.quantity}`,
                              )
                              .join(", ") ?? "—"}
                          </td>
                          <td className="px-2 py-2">
                            {order.createdAt
                              ? new Date(order.createdAt).toLocaleString()
                              : "—"}
                          </td>
                          <td className="px-2 py-2">
                            {Number(order.amount).toLocaleString()}원
                          </td>
                          <td className="px-2 py-2">
                            <button
                              type="button"
                              onClick={() => setSelectedOrderId(order.id)}
                              className={clsx(
                                "p-2 rounded text-sm",
                                selectedOrderId === order.id
                                  ? "bg-gray-500 text-white"
                                  : "bg-blue-500 text-white hover:bg-blue-600",
                              )}
                            >
                              선택
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="p-4 bg-gray-100 rounded-lg">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    취소 사유 (4글자 이상)
                  </label>
                  <input
                    type="text"
                    value={cancelReason}
                    placeholder="취소 사유를 입력하세요"
                    onChange={(e) => setCancelReason(e.target.value)}
                    className="border p-2 rounded w-full text-sm"
                  />
                </div>

                {selectedOrderId && selectedOrder ? (
                  <>
                    {settlementBlocked ? (
                      <div className="text-amber-800 text-sm rounded border border-amber-200 bg-amber-50 p-3">
                        이 주문은 정산 요청·승인 또는 지급 연결이 있어 이 화면에서
                        환불할 수 없습니다. (토스 지급{" "}
                        <code className="text-xs bg-amber-100 px-1">FAILED</code>{" "}
                        재시도 전만 예외적으로 허용)
                      </div>
                    ) : null}

                    <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                      <div className="text-sm font-semibold text-gray-800">
                        메뉴별 부분환불
                      </div>
                      <p className="text-xs text-gray-600">
                        취소할 수량을 입력한 뒤 실행하세요. 전체 수량을 없애면
                        안 됩니다 → 전체 취소는 아래 버튼을 이용하세요.
                      </p>
                      <div className="space-y-2">
                        {selectedOrder.orderItems?.map((oi) => (
                          <label
                            key={oi.id}
                            className="flex flex-wrap items-center gap-2 text-sm text-gray-800"
                          >
                            <span className="min-w-0 flex-1">
                              {oi.product?.name ?? "상품"}{" "}
                              <span className="text-gray-500">
                                (주문 {oi.quantity}개)
                              </span>
                            </span>
                            <span className="text-gray-500 shrink-0">취소</span>
                            <input
                              type="number"
                              inputMode="numeric"
                              min={0}
                              max={oi.quantity}
                              placeholder="0"
                              value={partialLineQtyStr[oi.id] ?? ""}
                              onChange={(e) =>
                                setPartialLineQtyStr((prev) => ({
                                  ...prev,
                                  [oi.id]: e.target.value,
                                }))
                              }
                              className="w-16 border rounded px-1 py-0.5 text-sm"
                              disabled={settlementBlocked}
                            />
                          </label>
                        ))}
                      </div>
                      {partialInvalidQty ? (
                        <p className="text-xs text-red-600">
                          취소 수량은 0 이상·주문 수량 이하여야 합니다.
                        </p>
                      ) : null}
                      {partialHasSelection &&
                      !partialLeavesAtLeastOneQty ? (
                        <p className="text-xs text-red-600">
                          최소 한 개 이상의 메뉴가 주문에 남아야 합니다. 전체 취소는
                          아래 버튼을 이용하세요.
                        </p>
                      ) : null}
                      <button
                        type="button"
                        disabled={!partialCanSubmit}
                        onClick={() => handlePartialMenuCancel()}
                        className={clsx(
                          "w-full py-2.5 rounded-lg text-sm font-bold text-white",
                          partialCanSubmit
                            ? "bg-indigo-600 hover:bg-indigo-700"
                            : "bg-gray-400 cursor-not-allowed",
                        )}
                      >
                        선택 메뉴 부분환불 (PG)
                      </button>
                    </div>

                    <button
                      type="button"
                      disabled={
                        settlementBlocked || cancelReason.trim().length < 4
                      }
                      onClick={() => handleCancelOrder(selectedOrder.id)}
                      className={clsx(
                        "w-full py-3 text-white font-bold rounded-lg",
                        settlementBlocked || cancelReason.trim().length < 4
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-red-500 hover:bg-red-600",
                      )}
                    >
                      실시간 주문 전체 취소 (환불 진행)
                    </button>
                  </>
                ) : null}
              </>
            )}
          </div>
        ) : null,
      }}
    />
  );
}
